import network from '@/services/network';
import {
  ApiResponse,
  ErgebnisWandAntwort,
  AvailableSlot,
  GoogleCalendarEvent,
  Meeting,
  MeetingTopic,
  OutcomeType,
  PaginatedResponse,
  PendingMeeting,
  SpecialistInfo,
  WartendeKundenResponse,
} from '@/types';
import { ErrorResponse } from '@/utils';

const getMeetings = async (params = {}): Promise<PaginatedResponse<Meeting>> => {
  try {
    const { data } = await network.get<PaginatedResponse<Meeting>>('/meetings', { params });
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const getMeeting = async (id: number): Promise<ApiResponse<Meeting>> => {
  try {
    const { data } = await network.get<ApiResponse<Meeting>>(`/meetings/${id}`);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const updateMeeting = async (id: number, meetingData: Partial<Meeting>): Promise<ApiResponse<Meeting>> => {
  try {
    const { data } = await network.put<ApiResponse<Meeting>>(`/meetings/${id}`, meetingData);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const deleteMeeting = async (id: number): Promise<void> => {
  try { await network.delete(`/meetings/${id}`); }
  catch (error) { throw new ErrorResponse(error); }
};

const setOutcome = async (meetingId: number, type: OutcomeType, reason?: string): Promise<ApiResponse<Meeting>> => {
  try {
    const { data } = await network.post<ApiResponse<Meeting>>(
      `/meetings/${meetingId}/outcome`,
      { type, reason },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * Ergebnis zurueck auf OFFEN (B1, Auftrag Alex 17.08.2026).
 *
 * DELETE und nicht `setOutcome(null)`: „offen" ist die ABWESENHEIT eines
 * Ergebnisses, kein Wert. Ein Enum-Wert `open` haette jede Auswertung
 * anfassen muessen, die heute `outcome_type IS NULL` fragt — darunter die
 * Slot-Freigabe.
 */
const clearOutcome = async (meetingId: number): Promise<ApiResponse<Meeting>> => {
  try {
    const { data } = await network.delete<ApiResponse<Meeting>>(`/meetings/${meetingId}/outcome`);
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * Farbe eines eigenen Termins setzen (docs/tasks/kalender-farben.md).
 * Raw throw — der Kalender behandelt den Fehler inline (optimistischer
 * Rollback + Toast), ein generischer Netzwerk-Toast waere hier irrefuehrend.
 */
/**
 * Terminart nachtraeglich aendern — light (W10, Decision #211, 19.09.2026).
 *
 * Eigener Endpoint wie `/color`: PUT /meetings/{id} lehnt `meeting_category`
 * seitdem ab. Erlaubt sind nur die Ziele aus `meeting.typwechsel_ziele`; der
 * Server prueft dieselbe Regel und antwortet mit 422 (Kombination) bzw. 409
 * (vergangen / mit Ergebnis) und Klartext in `message`.
 */
const setMeetingTyp = async (meetingId: number, meetingCategory: string): Promise<ApiResponse<Meeting>> => {
  try {
    const { data } = await network.patch<ApiResponse<Meeting>>(
      `/meetings/${meetingId}/typ`,
      { meeting_category: meetingCategory },
    );
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const setMeetingColor = async (meetingId: number, colorId: string): Promise<void> => {
  await network.patch(`/meetings/${meetingId}/color`, { color_id: colorId });
};

const getCalendarMeetings = async (from: string, to: string): Promise<GoogleCalendarEvent[]> => {
  try {
    const { data } = await network.get<GoogleCalendarEvent[]>('/meetings/calendar', {
      params: { from, to },
    });
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

// Raw throw — caller handles errors inline
const getMeetingTopics = async (): Promise<{ data: MeetingTopic[] }> => {
  const { data } = await network.get<{ data: MeetingTopic[] }>('/meeting-topics');
  return data;
};

// Raw throw — dialog needs error.response?.data?.error?.code
const resolveSpecialist = async (
  customerId: number,
  topicId: number,
  // Fix 30.07.2026: Im Case ist der Fall bekannt — mitgeschickt gewinnt sein
  // Owner, auch wenn der Fall geschlossen ist. Ohne das Feld unveraendert.
  dealId?: number | null,
  // PILOT Folgetermin-Umleitung (Decision #196): erst mit der Terminart laesst
  // sich beantworten, ob diese Buchung umgeleitet wird. Optional — ohne das
  // Feld antwortet der Server exakt wie bisher.
  meetingCategory?: string | null,
): Promise<SpecialistInfo> => {
  const { data } = await network.post<SpecialistInfo>('/meetings/resolve-specialist', {
    customer_id: customerId,
    topic_id: topicId,
    ...(dealId ? { deal_id: dealId } : {}),
    ...(meetingCategory ? { meeting_category: meetingCategory } : {}),
  });
  return data;
};

// Raw throw — dialog shows loading error inline
const availableSlots = async (
  userId: number,
  date: string,
  durationMinutes: number,
  // Selbstbuchungs-Kalender (31.07.2026): optionaler 15er-Takt.
  stepMinutes?: number,
  /**
   * Fall-Bezug (Decision #165, 17.08.2026). Steht der Dialog in einem Fall,
   * wird die `deal_id` mitgeschickt und serverseitig autorisiert
   * (`AvailableSlotsRequest::authorize` -> `DealPolicy::bookMeeting`). Ohne
   * Fall — Themen-Buchungs-Modus — bleibt sie weg.
   *
   * Zweck ist Deckungsgleichheit: die Anzeige soll nichts liefern, was
   * `POST /meetings` anschliessend mit 403 ablehnt.
   */
  dealId?: number | null,
): Promise<{ slots: AvailableSlot[]; user: { id: number; display_name: string } }> => {
  const { data } = await network.get('/meetings/available-slots', {
    params: {
      user_id: userId,
      date,
      duration_minutes: durationMinutes,
      ...(stepMinutes ? { step_minutes: stepMinutes } : {}),
      ...(dealId ? { deal_id: dealId } : {}),
    },
  });
  return data;
};

export interface BookMeetingPayload {
  customer_id: number;
  deal_id?: number | null;
  topic_id?: number | null;
  scheduled_at: string;
  /**
   * Berater-gebundener Slot-Token — optional seit 09.08.2026 (Decision #112).
   *
   * Auf dem Themen-/Pool-Weg gibt es keinen: der Token ist ein HMAC ueber
   * (user, start, duration), und poolweit steht der User vorher nicht fest —
   * er faellt erst beim Buchen per WRR. Der Buchungskern behandelt den Wert
   * bereits als optional (`if (! empty($payload['slot_token']))`); die
   * Slot-Wahrheit ist dort das Live-Gate gegen Google, nicht der Token.
   */
  slot_token?: string;
  expected_specialist_id?: number | null;
  meeting_category?: string;
  booked_by_user_id?: number | null;
  // Selbstbuchungs-Kalender (31.07.2026): nur Eigenbuchung.
  duration_minutes?: number;
  notes?: string;
  /**
   * Meeting-Titel aus dem Buchungsfenster (Entscheid Alex 03.08.2026).
   *
   * Darf LEER sein — der Server setzt dann den generierten Titel ein
   * (`MeetingBookingService::persistMeeting`). Deshalb `string` und nicht
   * `string | undefined` im Sinne von "weglassen": der Dialog schickt den
   * Wert immer mit, auch geleert, und die Auslegung passiert an einer
   * Stelle im Backend.
   */
  title?: string;
  /**
   * Gewaehlte Erinnerungs-Baender (Entscheid Alex 03.08.2026), Werte aus dem
   * Backend-Enum `ReminderTypeEnum`: `7d` | `3d` | `1d` | `10min`.
   *
   * WEGLASSEN und LEERES ARRAY bedeuten Verschiedenes: fehlt der Schluessel,
   * bleiben alle Baender an (Default, alle uebrigen Buchungswege); ein leeres
   * Array heisst "keine Erinnerung" und ist eine bewusste Wahl.
   */
  reminder_types?: string[];
  /**
   * Wiederholschutz, Variante C (Entscheid Alex 14.08.2026,
   * docs/tasks/buchungskern-haertung.md Teil 2): UUID je Absende-Absicht,
   * erzeugt von `@/utils/absendeAbsicht`. Ueber Fehlversuche hinweg stabil,
   * erst nach dem Erfolg beendet — nur so traegt der Wiederholversuch
   * denselben Schluessel.
   *
   * OPTIONAL: fehlt er, faellt der Server auf einen deterministischen
   * Schluessel zurueck. Kein Aufrufer bricht dadurch.
   */
  idempotency_key?: string;
}

// Raw throw — dialog checks error.response?.data?.error?.code for drift errors
const bookMeeting = async (payload: BookMeetingPayload): Promise<{ data: Record<string, unknown> }> => {
  const { data } = await network.post<{ data: Record<string, unknown> }>('/meetings', payload);
  return data;
};

// Raw throw — Dialog zeigt die konkrete Server-Fehlermeldung (422/429/503) als Toast.
const resendReminder = async (
  meetingId: number,
  type: string,
): Promise<{ message: string; customer_email: string | null }> => {
  const { data } = await network.post(`/meetings/${meetingId}/reminders/${type}/resend`);
  return data;
};

/** Ergebnis-Wand (Decision #245) — faellige eigene Meetings ab dem Wand-Stichtag (26.10.2026). */
const getPendingOutcomes = async (): Promise<ErgebnisWandAntwort<PendingMeeting>> => {
  try {
    const { data } = await network.get<ErgebnisWandAntwort<PendingMeeting>>('/meetings/pending-outcomes');
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

/** Popup „Mandant wartet" — nur eigene Termine, Schalter serverseitig. */
const getWartendeKunden = async (): Promise<WartendeKundenResponse> => {
  try {
    const { data } = await network.get<WartendeKundenResponse>('/meetings/wartende-kunden');
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

/**
 * Hat derselbe Kunde zur gewaehlten Zeit schon einen offenen Termin (Decision
 * #254 A)? Nur fuer den Hinweis im Buchen-Dialog, sperrt nichts. `weitere`
 * zaehlt Termine ausserhalb der eigenen Sicht, dazu gibt es keine Details.
 */
export interface KundenTerminUeberlappung {
  termine: { scheduled_at: string; kategorie: string | null }[];
  weitere: number;
}

const kundenTerminUeberlappung = async (
  customerId: number,
  start: string,
  ende: string,
): Promise<KundenTerminUeberlappung> => {
  try {
    const { data } = await network.get<KundenTerminUeberlappung>('/meetings/kunden-termin-ueberlappung', {
      params: { customer_id: customerId, start, ende },
    });
    return data;
  } catch (error) { throw new ErrorResponse(error); }
};

const meetingApi = {
  kundenTerminUeberlappung,
  getMeetings,
  resendReminder,
  getMeeting,
  updateMeeting,
  deleteMeeting,
  setOutcome,
  clearOutcome,
  getPendingOutcomes,
  setMeetingTyp,
  setMeetingColor,
  getCalendarMeetings,
  getMeetingTopics,
  resolveSpecialist,
  availableSlots,
  bookMeeting,
  getWartendeKunden,
};

export default meetingApi;
